// ABOUTME: Single-line text field with a view/edit toggle, mirroring CTextAreaWithEdit.
// ABOUTME: Both the view and the editor show the server translation — see the l10n prop.

import { useState } from "react"
import { CLabel } from "./CLabel"
import { CToggleSwitch } from "./CToggleSwitch"
import { Controller, type Control, type FieldValues, type Path, type UseFormGetValues } from "react-hook-form"
import { PencilGrayIcon } from "@shared/icons"
import type { L10nFragment } from "@/graphql/main/gql/graphql"
import { CFieldText } from "./CFieldText"
import { L10nEditWarning } from "./l10n/L10nEditWarning"
import { L10nNote } from "./l10n/L10nNote"
import { resolveL10n } from "./l10n/fn"

interface IProps<TFieldValues extends FieldValues = FieldValues> {
  control: Control<TFieldValues>
  valName: Path<TFieldValues>
  getValues: UseFormGetValues<TFieldValues> | undefined
  label?: string
  placeholder?: string
  required?: boolean
  color?: string
  helperText?: string
  disabled?: boolean
  className?: string
  /**
   * Server-localized twin of this field. The user reads and edits the text in
   * their own language: the translation is prefilled, and saving stores it as
   * the text itself, making the editor's language the new original (WA-762).
   */
  l10n?: L10nFragment | null
}

const CFieldTextWithEdit = <TFieldValues extends FieldValues = FieldValues>(props: IProps<TFieldValues>) => {
  const { control, valName, getValues, label, placeholder, required, color, helperText, disabled, className, l10n } =
    props
  // Whatever the form holds under this path; only a string is text to this field.
  const val: unknown = getValues?.(valName)
  const valText = typeof val === "string" ? val : null
  // Start in edit mode only when there's nothing to read yet.
  const [isEditing, setIsEditing] = useState(!val)
  const [showOriginal, setShowOriginal] = useState(false)
  // What the user has typed since entering edit mode; null until the first keystroke.
  const [draft, setDraft] = useState<string | null>(null)
  // Every keystroke writes the draft into the form too, so the draft is also the
  // value we last wrote there. When the form value changes to anything else, the
  // record itself was swapped (another entry, a refetch or an external reset)
  // and the stale draft must not leak into it.
  if (draft !== null && valText !== draft) {
    setDraft(null)
  }
  const l10nView = resolveL10n(l10n, valText ?? "")
  const readOnlyText = showOriginal ? l10nView.original : l10nView.display
  // Editing a machine translation replaces the author's original on save.
  const editsTranslation = l10nView.badge === "translated" && l10nView.canShowOriginal

  return (
    <div className="relative">
      {/* Inside a disabled fieldset the switch is disabled too, so a read-only
          form (system plan, no subscription) stays on the translated view. */}
      <div className="absolute right-0 -top-2 flex items-center bg-white p-1 pl-2.5 rounded-full">
        <PencilGrayIcon className="mr-2.5" />
        <CToggleSwitch
          checked={isEditing}
          onChange={() => setIsEditing(!isEditing)}
        />
      </div>
      {label && (
        <CLabel className="block max-md:max-w-52 mb-1 text-gray-900 text-sm font-medium leading-4">{label}</CLabel>
      )}
      {isEditing ? (
        <Controller
          control={control}
          name={valName}
          rules={{ required }}
          render={({ field: { value, onChange } }) => (
            <>
              <CFieldText
                // Edit the text the user actually reads. `draft` stays null
                // until the first keystroke, so the form value (the author's
                // original) is untouched — and therefore not dirty — while the
                // user merely looks at the field. See the warning below.
                value={draft ?? (editsTranslation ? l10nView.display : ((value as string | undefined) ?? ""))}
                onChange={(e) => {
                  setDraft(e.target.value)
                  onChange(e.target.value)
                }}
                placeholder={placeholder}
                color={color}
                helperText={helperText}
                disabled={disabled}
                className={`bg-white ${className ?? ""}`}
              />
              {editsTranslation && <L10nEditWarning />}
            </>
          )}
        />
      ) : (
        <>
          <div className="flex items-center min-h-[30px] bg-customGray-100 border border-gray-300 rounded-lg px-2.5 py-1 text-sm text-gray-900 wrap-anywhere">
            {readOnlyText || <span className="text-gray-400">{placeholder}</span>}
          </div>
          <L10nNote
            view={l10nView}
            showingOriginal={showOriginal}
            onToggle={() => setShowOriginal(!showOriginal)}
          />
        </>
      )}
    </div>
  )
}

export default CFieldTextWithEdit
export { CFieldTextWithEdit }
